// User-roles management screen + role editor (RBAC).
//
// One row per role; admins can create custom roles, edit non-system ones,
// and tick which module-level capabilities each role grants.
//
// Module/action matrix is intentionally fixed in code (MODULES below) —
// this is a UI for choosing among a known set of capabilities, not a
// generic IAM system. Adding a new capability later is one entry here
// plus matching gates wherever it's enforced.

import React from 'react';
import { Icon, Pill, Button, Card, Banner, Section } from './ui.jsx';
import { api } from './api.js';
import { hasPermission } from './auth.jsx';

// The capability matrix presented to admins. Keep groupings small and
// human-readable; describe what each capability does in plain English.
const MODULES = [
  {
    key: 'runs',
    label: 'Commission runs',
    actions: [
      { key: 'read',           label: 'View runs', desc: 'See the list of runs and the per-rep results.' },
      { key: 'create',         label: 'Create new runs', desc: 'Run the upload wizard and produce calculations.' },
      { key: 'edit',           label: 'Edit drafts', desc: 'Change rep assignments, override invoices, exclude rows.' },
      { key: 'finalize',       label: 'Finalize runs', desc: 'Lock the math so an approver can review.' },
      { key: 'approve',        label: 'Approve runs', desc: 'Sign off on a finalized run. Cannot approve own work.' },
      { key: 'reject_invoice', label: 'Reject individual invoices', desc: 'During approval, zero out a single invoice with a reason.' },
      { key: 'delete',         label: 'Delete drafts', desc: 'Permanently remove a draft run.' },
    ],
  },
  {
    key: 'tenants',
    label: 'Tenants',
    actions: [
      { key: 'read',   label: 'View tenants',     desc: 'See the tenant list and settings.' },
      { key: 'write',  label: 'Edit tenant',      desc: 'Update name, entities, and other settings.' },
      { key: 'create', label: 'Create new tenant',desc: 'Stand up a new tenant via the wizard.' },
    ],
  },
  {
    key: 'users',
    label: 'Users & roles',
    actions: [
      { key: 'read',         label: 'View users',     desc: 'See the user list and their assigned roles.' },
      { key: 'invite',       label: 'Invite users',   desc: 'Send an invite that creates auth + app rows.' },
      { key: 'edit',         label: 'Edit users',     desc: 'Change name, role assignment, per-rep rates.' },
      { key: 'manage_roles', label: 'Manage roles',   desc: 'Create or edit roles and their permissions.' },
    ],
  },
  {
    key: 'customers',
    label: 'Customers',
    actions: [
      { key: 'read',       label: 'View customers',     desc: 'Browse the customer directory.' },
      { key: 'edit',       label: 'Edit customers',     desc: 'Rename or otherwise modify a customer.' },
      { key: 'assign_rep', label: 'Assign rep to client', desc: 'Change which rep owns a customer.' },
    ],
  },
  {
    key: 'plans',
    label: 'Commission plans',
    actions: [
      { key: 'read',   label: 'View plans',  desc: 'See the tier ladders and deductions.' },
      { key: 'edit',   label: 'Edit plans',  desc: 'Create, update, or rename plans.' },
      { key: 'delete', label: 'Delete plans',desc: 'Remove a non-default plan.' },
    ],
  },
  {
    key: 'audit',
    label: 'Audit log',
    actions: [
      { key: 'read', label: 'View audit log', desc: 'See the immutable record of mutations.' },
    ],
  },
  {
    key: 'own_view',
    label: 'Self-service (rep view)',
    actions: [
      { key: 'read', label: 'See own commission', desc: 'Rep dashboard, history, unpaid AR. Required for rep-base roles.' },
    ],
  },
];

// Compact "yes/no" pill on the list row. We don't try to summarize the
// permissions in the list — there are too many — so this just calls out
// system roles (immutable) vs. custom (editable).
function RoleListRow({ role, isAdmin, onEdit }) {
  return (
    <tr className="hover:bg-primary-100/30">
      <td className="px-4 py-3">
        <div className="font-semibold text-neutral-900">{role.name}</div>
        {role.description && <div className="text-[12px] text-neutral-500 mt-0.5 max-w-[440px]">{role.description}</div>}
      </td>
      <td className="px-4 py-3 text-center">
        <Pill tone={role.baseRole === 'admin' ? 'brand' : 'y2'}>{role.baseRole === 'admin' ? 'Admin base' : 'Rep base'}</Pill>
      </td>
      <td className="px-4 py-3 text-center">
        {role.isSystemRole ? <Pill tone="finalized">System</Pill> : <Pill tone="neutral">Custom</Pill>}
      </td>
      <td className="px-4 py-3 text-right">
        {isAdmin && (
          <button onClick={onEdit} className="text-[12px] text-primary-700 hover:text-primary-900 font-medium inline-flex items-center gap-1">
            <Icon name={role.isSystemRole ? 'info' : 'override'} size={12}/>
            {role.isSystemRole ? 'View' : 'Edit'}
          </button>
        )}
      </td>
    </tr>
  );
}

// ---------- S26 · Roles list ----------
export function ScreenUserRoles({ onNav, currentUser }) {
  const [roles, setRoles]       = React.useState([]);
  const [loading, setLoading]   = React.useState(true);
  const [editing, setEditing]   = React.useState(null);   // role being edited (or 'new')
  const [creating, setCreating] = React.useState(false);
  // Manage-roles is the right gate here — controls creating, editing,
  // and deleting roles. Approver has only users.read, so she can VIEW
  // but not modify; Administrator + Sales Manager + anyone with the
  // explicit permission can.
  const isAdmin = hasPermission(currentUser, 'users', 'manage_roles');

  React.useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const list = await api.roles.list();
        if (alive) setRoles(list);
      } catch (err) { console.error('Roles load:', err); }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, []);

  const onSaved = (saved) => {
    setRoles(rs => {
      const without = rs.filter(r => r.id !== saved.id);
      return [...without, saved].sort((a, b) =>
        Number(b.isSystemRole) - Number(a.isSystemRole) || a.name.localeCompare(b.name));
    });
    setEditing(null); setCreating(false);
  };
  const onDeleted = (id) => {
    setRoles(rs => rs.filter(r => r.id !== id));
    setEditing(null);
  };

  if (loading) return <div className="p-10 text-center text-neutral-500 text-[13px]">Loading roles…</div>;

  if (editing) {
    return <RoleEditor
      role={editing === 'new' ? null : editing}
      currentUser={currentUser}
      onSaved={onSaved}
      onDeleted={onDeleted}
      onCancel={() => { setEditing(null); setCreating(false); }}/>;
  }

  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-[24px] font-bold text-neutral-900 tracking-[-0.01em]">Roles &amp; permissions</h1>
          <div className="text-[13px] text-neutral-500 mt-1 max-w-[640px]">
            Roles are named permission bundles. Assign one to each user
            (Users → Edit) to control which modules they can see and which
            actions they can take. System roles (Administrator, Sales Rep)
            can't be modified; create your own for everything in between.
          </div>
        </div>
        {isAdmin && (
          <Button variant="primary" icon="plus" onClick={() => setEditing('new')}>New role</Button>
        )}
      </div>

      {!isAdmin && (
        <Banner tone="info" icon="lock" title="Read-only">
          Only administrators can create or edit roles.
        </Banner>
      )}

      <Card padding="p-0">
        <table className="w-full text-[13px]">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-[0.06em] text-neutral-500 bg-canvas/60 border-b border-neutral-200">
              <th className="px-4 py-2.5 font-semibold">Role</th>
              <th className="px-4 py-2.5 font-semibold text-center">Base</th>
              <th className="px-4 py-2.5 font-semibold text-center">Type</th>
              <th className="px-4 py-2.5 w-24"/>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-200">
            {roles.map(r => (
              <RoleListRow key={r.id} role={r} isAdmin={isAdmin} onEdit={() => setEditing(r)}/>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

// ---------- Role editor ----------
function RoleEditor({ role, currentUser, onSaved, onDeleted, onCancel }) {
  const isNew      = !role;
  const isSystem   = !!role?.isSystemRole;
  const readOnly   = isSystem || currentUser?.role !== 'admin';

  const [name, setName]               = React.useState(role?.name || '');
  const [description, setDescription] = React.useState(role?.description || '');
  const [baseRole, setBaseRole]       = React.useState(role?.baseRole || 'admin');
  // Permissions: deep copy so toggling doesn't mutate the source object.
  const [perms, setPerms]             = React.useState(() => JSON.parse(JSON.stringify(role?.permissions || {})));
  const [confirmDelete, setConfirmDelete] = React.useState(false);
  const [saving, setSaving]           = React.useState(false);
  const [error, setError]             = React.useState(null);

  const has = (m, a) => {
    if (perms.all === true) return true;
    return perms[m]?.[a] === true;
  };
  const toggle = (m, a) => {
    if (readOnly) return;
    setPerms(p => {
      const next = { ...p, [m]: { ...(p[m] || {}) } };
      // Clearing the global 'all' shortcut — when an admin un-ticks
      // anything on a role with permissions={all:true}, snapshot the
      // current effective state into explicit module/action keys before
      // applying the toggle so the rest of the matrix still reflects
      // their original intent.
      if (next.all === true) {
        delete next.all;
        for (const M of MODULES) {
          next[M.key] = next[M.key] || {};
          for (const A of M.actions) {
            if (next[M.key][A.key] === undefined) next[M.key][A.key] = true;
          }
        }
      }
      next[m][a] = !next[m]?.[a];
      return next;
    });
  };

  const save = async () => {
    setError(null);
    if (!name.trim()) { setError('Give the role a name.'); return; }
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        description: description.trim() || null,
        baseRole,
        permissions: perms,
      };
      const saved = isNew
        ? await api.roles.create(payload)
        : await api.roles.update(role.id, payload);
      onSaved(saved);
    } catch (err) {
      setError('Save failed: ' + (err.message || err));
    } finally { setSaving(false); }
  };

  const remove = async () => {
    setError(null); setSaving(true);
    try {
      await api.roles.delete(role.id);
      onDeleted(role.id);
    } catch (err) {
      setSaving(false);
      setError('Delete failed: ' + (err.message || err));
    }
  };

  return (
    <div className="space-y-5 max-w-[860px]">
      <div className="flex items-end justify-between gap-4">
        <div>
          <button onClick={onCancel} className="text-[12px] text-neutral-500 hover:text-neutral-800 mb-1.5 inline-flex items-center gap-1">
            <Icon name="chevron" size={11} className="rotate-180"/> Back to roles
          </button>
          <h1 className="text-[24px] font-bold text-neutral-900 tracking-[-0.01em]">
            {isNew ? 'New role' : (isSystem ? `${role.name} (system)` : `Edit ${role.name}`)}
          </h1>
        </div>
      </div>

      {readOnly && (
        <Banner tone="info" icon="lock" title={isSystem ? 'System role — read-only' : 'Read-only'}>
          {isSystem
            ? 'Administrator and Sales Rep are baked into the product and cannot be modified. Create a custom role to express anything else.'
            : 'Only administrators can edit roles.'}
        </Banner>
      )}

      <Card className="space-y-4">
        <div className="grid grid-cols-1 gap-3">
          <div>
            <label className="block text-[12px] font-semibold text-neutral-700 mb-1.5">Role name</label>
            <input value={name} onChange={e => setName(e.target.value)} disabled={readOnly || saving}
              placeholder="e.g. Approver, Sales Manager, Read-only auditor"
              className="w-full h-10 px-3 text-[14px] rounded-md border border-neutral-300 focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-700 disabled:opacity-50 disabled:bg-neutral-50"/>
          </div>
          <div>
            <label className="block text-[12px] font-semibold text-neutral-700 mb-1.5">Description</label>
            <textarea rows={2} value={description} onChange={e => setDescription(e.target.value)} disabled={readOnly || saving}
              placeholder="One sentence describing who this role is for."
              className="w-full px-3 py-2 text-[13px] rounded-md border border-neutral-300 focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-700 disabled:opacity-50 disabled:bg-neutral-50"/>
          </div>
          <div>
            <label className="block text-[12px] font-semibold text-neutral-700 mb-1.5">Base role</label>
            <select value={baseRole} onChange={e => setBaseRole(e.target.value)} disabled={readOnly || saving || !isNew}
              className="w-full h-10 px-3 text-[13px] rounded-md border border-neutral-300 bg-white focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-700 disabled:opacity-50 disabled:bg-neutral-50">
              <option value="admin">Admin base — can act tenant-wide (most custom roles)</option>
              <option value="rep">Rep base — restricted to own data only</option>
            </select>
            <div className="text-[11.5px] text-neutral-500 mt-1.5 leading-relaxed">
              Drives the database row-level security: <i>admin base</i> roles can read tenant data,
              <i> rep base</i> roles only their own. Permissions below add finer gating on top of that.
              {!isNew && ' (Cannot change after creation.)'}
            </div>
          </div>
        </div>
      </Card>

      <Section title="Module permissions" sub="Tick the actions this role should be allowed to perform.">
        <div className="space-y-3">
          {MODULES.map(M => (
            <Card key={M.key} className="space-y-3">
              <div className="text-[14px] font-semibold text-neutral-900">{M.label}</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {M.actions.map(A => (
                  <label key={A.key}
                    className={`flex items-start gap-3 p-3 rounded-md border cursor-pointer transition-colors
                      ${has(M.key, A.key) ? 'border-primary-300 bg-primary-100/30' : 'border-neutral-200 hover:border-neutral-300'}
                      ${readOnly ? 'cursor-not-allowed opacity-70' : ''}`}>
                    <input
                      type="checkbox"
                      checked={has(M.key, A.key)}
                      onChange={() => toggle(M.key, A.key)}
                      disabled={readOnly || saving}
                      className="mt-1 w-4 h-4 accent-primary-700"/>
                    <div className="flex-1">
                      <div className="text-[13px] font-semibold text-neutral-900">{A.label}</div>
                      <div className="text-[11.5px] text-neutral-600 mt-0.5 leading-relaxed">{A.desc}</div>
                    </div>
                  </label>
                ))}
              </div>
            </Card>
          ))}
        </div>
      </Section>

      {error && <div className="text-[12px] text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</div>}

      {!readOnly && (
        <div className="flex items-center gap-2">
          {!isNew && (
            confirmDelete ? (
              <div className="flex items-center gap-2 mr-auto">
                <span className="text-[12px] text-red-800 font-medium">Delete this role? Users assigned to it will fall back to their base role.</span>
                <Button variant="destructive" size="sm" onClick={remove} disabled={saving}>{saving ? 'Deleting…' : 'Yes, delete'}</Button>
                <Button variant="ghost" size="sm" onClick={() => setConfirmDelete(false)} disabled={saving}>No</Button>
              </div>
            ) : (
              <Button variant="ghost" size="md" onClick={() => setConfirmDelete(true)} disabled={saving} className="mr-auto text-red-700 hover:text-red-900 hover:bg-red-50">
                Delete role
              </Button>
            )
          )}
          <Button variant="ghost" onClick={onCancel} disabled={saving}>Cancel</Button>
          <Button variant="primary" icon="check" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : (isNew ? 'Create role' : 'Save changes')}
          </Button>
        </div>
      )}
    </div>
  );
}
